import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
    Paper, 
    Typography, 
    TextField, 
    Button, 
    IconButton,
    Divider,
    Avatar,
    Box,
    Container,
    CircularProgress,
    Tooltip,
    Snackbar,
    Alert
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ShareIcon from '@mui/icons-material/Share';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useAuth } from '../../contexts/AuthContext';
import { useDispatch, useSelector } from 'react-redux';
import { getTale, deleteComment } from '../../actions/tales';
import moment from 'moment';
import { fetchComments, createComment, updateComment } from '../../api';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { extractTaleId } from '../../utils/urlUtils';
import Seo from '../SEO/Seo';
import { Filter } from 'bad-words';

const profanityFilter = new Filter();
const isProfane = (text) => {
    try {
        return profanityFilter.isProfane(text);
    } catch {
        return false;
    }
};
const TaleDetail = () => {
    const { id: encodedIdWithSlug } = useParams();
    const id = extractTaleId(encodedIdWithSlug);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { user, isAuthenticated } = useAuth();
    const [currentUser, setCurrentUser] = useState(null);
    const [comments, setComments] = useState([]);
    const [newComment, setNewComment] = useState('');
    const [loading, setLoading] = useState(true);
    const [editingComment, setEditingComment] = useState(null);
    const [editedText, setEditedText] = useState('');
    const [shareCopied, setShareCopied] = useState(false);
    
    const tale = useSelector((state) => {
        return state.tales.tale;
    });

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                await dispatch(getTale(id));
                const commentsResponse = await fetchComments(id);
                setComments(commentsResponse.data);
            } catch (error) {
                // Handle error
            } finally {
                setLoading(false);
            }
        };
        
        fetchData();
    }, [id, dispatch]);

    useEffect(() => {
        // Try to get user from localStorage if not in context
        if (isAuthenticated) {
            const profile = localStorage.getItem('profile');
            if (profile) {
                const parsedProfile = JSON.parse(profile);
                setCurrentUser(parsedProfile);
            }
        }
    }, [user, isAuthenticated]);

    const handleSubmitComment = async (e) => {
        e.preventDefault();
        
        if (!isAuthenticated) {
            navigate('/auth');
            return;
        }

        if (!newComment.trim()) return;

        if (isProfane(newComment)) {
            // Soft block — keep the draft so the user can edit
            window.alert('Please remove inappropriate language before sharing your note.');
            return;
        }

        try {
            // Get token from localStorage using the current format
            const storedToken = localStorage.getItem('token');
            
            
            if (!storedToken) {
                return;
            }

            const response = await createComment(id, {
                text: newComment,
                user: user._id
            }, storedToken);
            
            
            const newCommentWithUser = {
                ...response.data,
                user: {
                    _id: user._id,
                    username: user.username,
                    imageUrl: user.imageUrl
                }
            };
            
            setComments(prevComments => [...prevComments, newCommentWithUser]);
            setNewComment('');
        } catch (error) {
            // Handle error silently
        }
    };

    const handleEditComment = (comment) => {
        setEditingComment(comment);
        setEditedText(comment.text);
    };

    const handleUpdateComment = async (commentId) => {
        try {
            if (isProfane(editedText)) {
                window.alert('Please remove inappropriate language before saving.');
                return;
            }
            await dispatch(updateComment(commentId, { 
                text: editedText,
                taleId: id
            }));
            
            const commentsResponse = await fetchComments(id);
            setComments(commentsResponse.data);
            
            setEditingComment(null);
            setEditedText('');
        } catch (error) {
            setEditingComment(null);
            setEditedText('');
        }
    };

    const handleDeleteComment = async (commentId) => {
        if (window.confirm('Are you sure you want to delete this comment?')) {
            try {
                await dispatch(deleteComment(commentId, id));
                
                // Refresh comments after deletion
                const commentsResponse = await fetchComments(id);
                setComments(commentsResponse.data);
            } catch (error) {
                // Handle error silently
            }
        }
    };



    const handleShare = async () => {
        const url = window.location.href;
        const shareText = tale
            ? `"${tale.title}" on Rejection Tales`
            : 'Check out this rejection story on Rejection Tales';

        if (navigator.share) {
            try {
                await navigator.share({ title: tale?.title, text: shareText, url });
            } catch (_) { /* user cancelled */ }
        } else {
            await navigator.clipboard.writeText(url);
            setShareCopied(true);
        }
    };

    const formatDate = (date) => {
        const now = new Date();
        const postedDate = new Date(date);
        const diffInSeconds = Math.floor((now - postedDate) / 1000);
        const diffInMinutes = Math.floor(diffInSeconds / 60);
        const diffInHours = Math.floor(diffInMinutes / 60);
        const diffInDays = Math.floor(diffInHours / 24);
        const diffInMonths = Math.floor(diffInDays / 30);
        const diffInYears = Math.floor(diffInDays / 365);

        if (diffInYears > 0) {
            return `${diffInYears} year${diffInYears > 1 ? 's' : ''} ago`;
        } else if (diffInMonths > 0) {
            return `${diffInMonths} month${diffInMonths > 1 ? 's' : ''} ago`;
        } else if (diffInDays > 0) {
            return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
        } else if (diffInHours > 0) {
            return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
        } else if (diffInMinutes > 0) {
            return `${diffInMinutes} minute${diffInMinutes > 1 ? 's' : ''} ago`;
        } else {
            return 'Just now';
        }
    };

    const renderComment = (comment) => {
        const isCommentCreator = isAuthenticated && comment.user?._id === user?._id;
        const isEditing = editingComment?._id === comment._id;

        return (
            <Box key={comment._id} sx={{ mb: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                    <Box display="flex" alignItems="center" mb={1}>
                        <Avatar 
                            src={comment.user?.imageUrl} 
                            alt={comment.user?.username || 'anonymous'}
                            sx={{ width: 24, height: 24, mr: 1 }}
                        >
                            {comment.user?.username?.[0]?.toUpperCase() || 'A'}
                        </Avatar>
                        <Typography variant="subtitle2" color="textSecondary" sx={{ fontWeight: 500 }}>
                            @{comment.user?.username || 'anonymous'} • {formatDate(comment.createdDate)}
                        </Typography>
                    </Box>
                    {isCommentCreator && (
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <IconButton 
                                size="small" 
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleEditComment(comment);
                                }}
                                sx={{ color: 'primary.main' }}
                            >
                                <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton 
                                size="small" 
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteComment(comment._id);
                                }}
                                sx={{ color: 'error.main' }}
                            >
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    )}
                </Box>
                {isEditing ? (
                    <Box sx={{ mt: 1 }}>
                        <TextField
                            fullWidth
                            multiline
                            rows={3}
                            value={editedText}
                            onChange={(e) => setEditedText(e.target.value)}
                            variant="outlined"
                            size="small"
                            sx={{ mb: 1 }}
                        />
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                            <Button 
                                size="small"
                                onClick={() => setEditingComment(null)}
                            >
                                Cancel
                            </Button>
                            <Button 
                                size="small"
                                variant="contained"
                                onClick={() => handleUpdateComment(comment._id)}
                                disabled={!editedText.trim() || editedText === comment.text}
                            >
                                Save
                            </Button>
                        </Box>
                    </Box>
                ) : (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                        {comment.text}
                    </Typography>
                )}
            </Box>
        );
    };

    if (loading) {
        return (
            <Container style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
                <CircularProgress />
            </Container>
        );
    }


    if (!tale) {
        return (
            <Container>
                <Typography variant="h6">Tale not found</Typography>
                <pre>{JSON.stringify({ id, loading, tale }, null, 2)}</pre>
            </Container>
        );
    }

    const storyExcerpt = tale.story?.slice(0, 160).replace(/\n/g, ' ');

    return (
        <Container style={{ padding: '24px' }}>
            {/* Per-tale SEO */}
            <Seo
                title={tale.title}
                description={storyExcerpt}
                path={`/tales/${encodedIdWithSlug}`}
                type="article"
                schema={{
                    '@context': 'https://schema.org',
                    '@type': 'Article',
                    headline: tale.title,
                    description: storyExcerpt,
                    datePublished: tale.createdDate,
                    author: { '@type': 'Person', name: tale.creator?.username || 'Anonymous' },
                    publisher: { '@type': 'Organization', name: 'Rejection Tales' }
                }}
            />

            {/* Top bar */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <IconButton onClick={() => navigate(-1)} color="primary">
                    <ArrowBackIcon />
                    <Typography variant="button" style={{ marginLeft: '8px' }}>Back</Typography>
                </IconButton>

                <Tooltip title="Share this story">
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<ShareIcon />}
                        onClick={handleShare}
                        sx={{ borderRadius: '20px', textTransform: 'none' }}
                    >
                        Share
                    </Button>
                </Tooltip>
            </Box>

            <Paper 
                elevation={3} 
                style={{ 
                    padding: '32px',
                    marginBottom: '24px',
                    borderTop: `6px solid ${tale.groupColor || '#3498db'}`
                }}
            >
                <Typography 
                    variant="h4" 
                    gutterBottom
                    style={{ color: tale.groupColor || '#3498db', fontWeight: '500' }}
                >
                    {tale.title}
                </Typography>
                
                <Typography 
                    variant="body1" 
                    style={{ 
                        marginBottom: '24px',
                        whiteSpace: 'pre-wrap',
                        color: '#333',
                        fontSize: '16px',
                        lineHeight: '1.6'
                    }}
                >
                    {tale.story}
                </Typography>

                <Typography 
                    variant="caption" 
                    color="textSecondary"
                    style={{ display: 'block', marginTop: '16px' }}
                >
                    Posted {formatDate(tale.createdDate)} in {tale.group}
                    {tale.creator?.username && ` · by @${tale.creator.username}`}
                </Typography>
            </Paper>

            <Typography variant="h5" gutterBottom style={{ marginTop: '32px' }}>
                Comments ({comments.length})
            </Typography>

            {comments.map(renderComment)}

            {/* Comment form — gated for guests */}
            {isAuthenticated ? (
                <Paper elevation={2} style={{ padding: '20px', marginBottom: '24px' }}>
                    <form onSubmit={handleSubmitComment}>
                        <TextField
                            fullWidth
                            multiline
                            rows={3}
                            variant="outlined"
                            placeholder="Add a comment..."
                            value={newComment}
                            onChange={(e) => setNewComment(e.target.value)}
                            style={{ marginBottom: '16px' }}
                        />
                        <Button 
                            variant="contained" 
                            color="primary" 
                            type="submit"
                            disabled={!newComment.trim()}
                        >
                            Post Comment
                        </Button>
                    </form>
                </Paper>
            ) : (
                <Paper 
                    elevation={0} 
                    style={{ 
                        padding: '24px', 
                        marginBottom: '24px', 
                        textAlign: 'center',
                        border: '1px solid rgba(0,0,0,0.1)',
                        borderRadius: '12px',
                        background: 'linear-gradient(135deg, #f8f9ff 0%, #f0f4ff 100%)'
                    }}
                >
                    <Typography variant="h6" gutterBottom>
                        Join the conversation
                    </Typography>
                    <Typography variant="body2" color="textSecondary" style={{ marginBottom: '16px' }}>
                        Sign in to share your thoughts, leave a comment, or post your own rejection story.
                    </Typography>
                    <Button
                        component={Link}
                        to="/auth"
                        variant="contained"
                        color="primary"
                        sx={{ borderRadius: '20px', textTransform: 'none', mr: 1 }}
                    >
                        Sign in
                    </Button>
                    <Button
                        component={Link}
                        to="/create-account"
                        variant="outlined"
                        color="primary"
                        sx={{ borderRadius: '20px', textTransform: 'none' }}
                    >
                        Create account
                    </Button>
                </Paper>
            )}

            {/* Share copied snackbar */}
            <Snackbar
                open={shareCopied}
                autoHideDuration={3000}
                onClose={() => setShareCopied(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity="success" onClose={() => setShareCopied(false)} sx={{ borderRadius: '20px' }}>
                    Link copied to clipboard!
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default TaleDetail; 